import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';

import OktaLogin from './components/OktaLogin';
import ErrorBoundary from './components/ErrorBoundary';

import { ThemeProvider } from './contexts/ThemeContext';
import MuiThemeWrapper from './components/ThemeProvider';

import oktaAuth from './config/oktaConfig';
import oktaService from './services/oktaService';
import logger from './utils/logger';

const ChatInterfaceAgent = lazy(() => import('./components/ChatInterfaceAgent'));
const OktaCallback = lazy(() => import('./components/OktaCallback'));

const LoadingFallback = () => (
  <div className="App">
    <div className="loading-container">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
);

function AppContent() {
  const [authState, setAuthState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to Okta auth state changes
  useEffect(() => {
    // Initialize Okta service
    oktaService.initialize(oktaAuth);

    // Get initial auth state
    const updateAuthState = async () => {
      try {
        logger.debug('[AUTH] Checking authentication status...');

        // Check if we're on the callback route - don't interfere but allow rendering
        if (window.location.pathname === '/login/callback') {
          logger.debug('[AUTH] On callback route, skipping auth check');
          setAuthState({ isAuthenticated: false, isPending: false, error: null });
          setIsLoading(false);
          return;
        }

        const isAuthenticated = await oktaAuth.isAuthenticated();
        logger.debug('[AUTH] isAuthenticated:', isAuthenticated);

        if (isAuthenticated) {
          const idToken = await oktaAuth.tokenManager.get('idToken');
          const accessToken = await oktaAuth.tokenManager.get('accessToken');

          logger.debug('[AUTH] Tokens found:', { idToken: !!idToken, accessToken: !!accessToken });

          const newAuthState = {
            isAuthenticated: true,
            idToken,
            accessToken,
            isPending: false,
            error: null
          };

          setAuthState(newAuthState);
        } else {
          logger.debug('[AUTH] Not authenticated');
          setAuthState({ isAuthenticated: false, isPending: false, error: null });
        }

        setIsLoading(false);
      } catch (error) {
        logger.error('[AUTH] Error getting auth state:', error);
        setAuthState({ isAuthenticated: false, isPending: false, error });
        setIsLoading(false);
      }
    };

    // Handler for auth state changes
    const handleAuthStateChange = (newAuthState) => {
      logger.debug('[AUTH] Auth state changed:', newAuthState?.isAuthenticated);
      if (newAuthState) {
        setAuthState({
          isAuthenticated: newAuthState.isAuthenticated,
          idToken: newAuthState.idToken,
          accessToken: newAuthState.accessToken,
          isPending: false,
          error: newAuthState.error || null
        });
        setIsLoading(false);
      }
    };

    // Subscribe to auth state changes
    oktaAuth.authStateManager.subscribe(handleAuthStateChange);

    // Get initial state
    updateAuthState();

    return () => {
      // Unsubscribe using the handler reference
      oktaAuth.authStateManager.unsubscribe(handleAuthStateChange);
    };
  }, []);

  // Okta login handler
  const handleOktaLogin = async () => {
    try {
      logger.debug('[AUTH] Redirecting to Okta login...');
      await oktaAuth.signInWithRedirect();
    } catch (error) {
      logger.error('[AUTH] Login error:', error);
    }
  };

  // Okta logout handler
  const handleOktaLogout = async () => {
    try {
      logger.debug('[AUTH] Logging out...');
      await oktaAuth.signOut();
    } catch (error) {
      logger.error('[AUTH] Logout error:', error);
    }
  };

  // Get user info from Okta
  const getOktaUser = () => {
    if (authState?.idToken?.claims) {
      return {
        email: authState.idToken.claims.email,
        name: authState.idToken.claims.name,
        sub: authState.idToken.claims.sub
      };
    }
    return null;
  };

  // Safety check for authState - wait for it to initialize
  if (!authState) {
    logger.debug('[AUTH] AuthState is null - waiting for initialization...');
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Initializing authentication...</p>
          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
            If this takes too long, check browser console for errors.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: '15px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check for authentication errors
  if (authState.error) {
    logger.error('[AUTH] Authentication error:', authState.error);
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Authentication error: {authState.error.message}</p>
        </div>
      </div>
    );
  }

  if (authState.isPending || isLoading) {
    logger.debug('[AUTH] Auth is pending - showing loading');
    return <LoadingFallback />;
  }

  const isOktaAuthenticated = authState.isAuthenticated;
  const oktaUser = getOktaUser();

  return (
    <div className="App">
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundary>
          <Routes>
            {/* Okta OAuth callback route */}
            <Route path="/login/callback" element={<OktaCallback />} />

            {/* Root route - SQL Agent with Okta authentication */}
            <Route path="/" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <ChatInterfaceAgent user={oktaUser} onLogout={handleOktaLogout} />
              )
            } />

            {/* Agent route - SQL Agent with Okta authentication */}
            <Route path="/agent" element={
              !isOktaAuthenticated ? (
                <OktaLogin onLogin={handleOktaLogin} />
              ) : (
                <ChatInterfaceAgent user={oktaUser} onLogout={handleOktaLogout} />
              )
            } />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </div>
  );
}

function AppOkta() {
  return (
    <ThemeProvider>
      <MuiThemeWrapper>
        <Router>
          <AppContent />
        </Router>
      </MuiThemeWrapper>
    </ThemeProvider>
  );
}

export default AppOkta;
